// qr-generator-org-picker — dependent location <select>, enabled once an
// organization is chosen. Styled to match the plain native <select> pattern
// already used elsewhere in the admin app (see AssignPlanDialog's plan/
// collection-method selects) rather than introducing a second combobox —
// per-org location counts are small, so a searchable widget isn't needed.

export interface LocationSelectOption {
  id: string;
  name: string;
}

export interface LocationSelectProps {
  id: string;
  value: string;
  onChange: (id: string) => void;
  options: LocationSelectOption[];
  /** Whether an organization has been chosen yet. */
  orgSelected: boolean;
  loading: boolean;
  placeholder: string;
  loadingLabel: string;
  /** Shown as the sole (disabled) option when the org has zero active locations. */
  emptyLabel?: string;
  'aria-describedby'?: string;
}

export function LocationSelect({
  id,
  value,
  onChange,
  options,
  orgSelected,
  loading,
  placeholder,
  loadingLabel,
  emptyLabel,
  'aria-describedby': ariaDescribedBy,
}: LocationSelectProps) {
  const disabled = !orgSelected || loading || options.length === 0;

  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-describedby={ariaDescribedBy}
      className="h-11 w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 text-[14px] text-jq-charcoal shadow-sm transition focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50"
    >
      {!orgSelected && <option value="">{placeholder}</option>}
      {orgSelected && loading && <option value="">{loadingLabel}</option>}
      {orgSelected && !loading && options.length === 0 && (
        <option value="">{emptyLabel ?? placeholder}</option>
      )}
      {orgSelected && !loading && options.length > 0 && (
        <>
          <option value="" disabled hidden>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </>
      )}
    </select>
  );
}
